import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { CartContext } from '@/context/CartContext';
import { WishlistContext } from '@/context/WishlistContext';
import type { Product } from '@/mocks/products';

interface ProductCardProps {
  product: Product;
  compact?: boolean;
  showCommerce?: boolean;
}

export default function ProductCard({ product, compact, showCommerce = true }: ProductCardProps) {
  const { addItem } = useContext(CartContext);
  const { isInWishlist, toggleWishlist } = useContext(WishlistContext);
  const [added, setAdded] = useState(false);
  const wished = isInWishlist(product.id);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const defaultVariant = product.variants
      ? `${product.variants[0].label}: ${product.variants[0].options[0]}`
      : undefined;
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      image: product.images[0],
      quantity: 1,
      variant: defaultVariant,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product.id);
  };

  const discount = product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
    : 0;

  return (
    <Link
      to={`/product/${product.slug}`}
      className={`product-card block group bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 ${
        compact ? '' : ''
      }`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-secondary-100">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-400"
          loading="lazy"
        />

        {/* Badges */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
          {product.isNew && (
            <span className="px-2 py-0.5 text-[10px] font-label font-semibold uppercase tracking-wider bg-accent-500 text-primary-800 rounded-full">
              New
            </span>
          )}
          {product.isLimited && (
            <span className="px-2 py-0.5 text-[10px] font-label font-semibold uppercase tracking-wider bg-accent-700 text-background-50 rounded-full">
              Limited Edition
            </span>
          )}
          {product.isSet && (
            <span className="px-2 py-0.5 text-[10px] font-label font-semibold uppercase tracking-wider bg-secondary-400 text-primary-800 rounded-full">
              Set of {product.name.includes('3') ? '3' : '2'}
            </span>
          )}
        </div>

        {/* Wishlist */}
        <button
          onClick={handleWishlist}
          className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-background-50/80 backdrop-blur-sm flex items-center justify-center hover:bg-background-50 transition-colors cursor-pointer"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i
              className={`text-base ${
                wished ? 'ri-heart-fill text-accent-500' : 'ri-heart-line text-foreground-500'
              }`}
            ></i>
          </div>
        </button>
      </div>

      {/* Info */}
      <div className="p-3.5 md:p-4">
        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          <span className="text-[10px] font-label font-semibold uppercase tracking-wider text-accent-500">
            {product.style}
          </span>
          <span className="text-[10px] font-label text-foreground-400">·</span>
          <span className="text-[10px] font-label font-semibold uppercase tracking-wider text-accent-500">
            {product.occasion}
          </span>
        </div>

        {/* Name */}
        <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-800 leading-snug mb-2">
          {product.name}
        </h3>

        {/* Price */}
        {showCommerce && (
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-base md:text-lg font-heading font-bold text-primary-500">
              ₹{product.price}
            </span>
            {product.mrp > product.price && (
              <>
                <span className="text-xs text-foreground-400 line-through">
                  ₹{product.mrp}
                </span>
                <span className="text-[10px] font-label font-semibold text-accent-600">
                  {discount}% off
                </span>
              </>
            )}
          </div>
        )}

        {/* Low stock */}
        {showCommerce && product.isLowStock && (
          <p className="text-[11px] font-label text-accent-500 mb-2">
            Only a few left
          </p>
        )}

        {/* Add to Cart */}
        {showCommerce && (
          <button
            onClick={handleAddToCart}
            className={`w-full py-2.5 rounded-lg font-label font-semibold text-xs transition-all duration-200 cursor-pointer whitespace-nowrap ${
              added
                ? 'bg-accent-500 text-primary-800'
                : 'bg-primary-500 text-background-50 hover:bg-accent-500 hover:text-primary-800'
            }`}
          >
            {added ? 'Added to Cart ✓' : 'Add to Cart'}
          </button>
        )}
      </div>
    </Link>
  );
}